import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, X, Plus, FileText, Pencil, Eye, MoreVertical, Clock, Trash2, AlertTriangle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MarkdownText } from '@/components/chat/MarkdownText';
import { MAX_ARTICLE_BODY_CHARS, type KnowledgeBaseItem } from '@/services/knowledgeBase.service';

interface ArticleEditorProps {
  article: KnowledgeBaseItem;
  onSave: (updates: Partial<KnowledgeBaseItem>) => void;
  saving?: boolean;
  readOnly?: boolean;
  initialEditing?: boolean;
  onMarkPending?: () => void;
  onDelete?: () => void;
}

export default function ArticleEditor({ article, onSave, saving, readOnly, initialEditing, onMarkPending, onDelete }: ArticleEditorProps) {
  const [name, setName] = useState(article.name);
  const [body, setBody] = useState(article.body || '');
  const [tags, setTags] = useState<string[]>(article.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(initialEditing && !readOnly);

  useEffect(() => {
    setName(article.name);
    setBody(article.body || '');
    setTags(article.tags || []);
    setTagInput('');
    setDirty(false);
    setEditing(initialEditing && !readOnly);
  }, [article.id]);

  const markDirty = useCallback(() => setDirty(true), []);

  const overLimit = body.length > MAX_ARTICLE_BODY_CHARS;

  const handleSave = () => {
    onSave({
      id: article.id,
      name: name.trim() || article.name,
      body,
      tags,
    });
    setDirty(false);
  };

  const addTag = (raw: string) => {
    const tag = raw.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags(prev => [...prev, tag]);
      markDirty();
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag));
    markDirty();
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <Input
          value={name}
          onChange={e => { setName(e.target.value); markDirty(); }}
          className="text-lg font-semibold border-none shadow-none px-0 h-auto focus-visible:ring-0"
          placeholder="Article title"
          readOnly={readOnly}
        />
        {!readOnly && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(prev => !prev)}
            >
              {editing ? <><Eye className="h-4 w-4 mr-1" />Preview</> : <><Pencil className="h-4 w-4 mr-1" />Edit</>}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !dirty || overLimit}
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {article.status === 'active' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onMarkPending}>
                    <Clock className="h-4 w-4 mr-2" />
                    Mark as Pending
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
      </div>

      {overLimit && (
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            This article is {body.length.toLocaleString()} characters — over the{' '}
            {MAX_ARTICLE_BODY_CHARS.toLocaleString()} character limit. Long articles overload
            Ellie's context, so it can't be saved. Shorten it or split it into multiple
            focused articles.
          </span>
        </div>
      )}

      <div className="px-4 py-2 border-b">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-xs font-bold">Tags</span>
          <span className="text-xs text-muted-foreground">Tags help Ellie find relevant information</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map(tag => (
            <Badge key={tag} variant="secondary" className="gap-1">
              {tag}
              {!readOnly && (
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => removeTag(tag)}
                />
              )}
            </Badge>
          ))}
          {!readOnly && (
            <div className="flex items-center gap-1">
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
                placeholder="Add tag..."
                className="h-6 w-24 text-xs border-none shadow-none px-0 focus-visible:ring-0"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-8 py-6">
        {editing ? (
          <Textarea
            value={body}
            onChange={e => { setBody(e.target.value); markDirty(); }}
            placeholder="Write your article in markdown..."
            className="h-full min-h-[200px] resize-none font-mono text-sm"
          />
        ) : body ? (
          <MarkdownText content={body} />
        ) : (
          <p className="text-muted-foreground italic">Nothing to preview</p>
        )}
      </div>
    </div>
  );
}
